import { getCookie } from "@app/actions";
import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
} from "axios";
import { isDebugCaptureEnabled } from "@/lib/debug-capture/env";
import { recordAxiosFailure } from "@/lib/debug-capture/failed-requests";
import {
  clearCoachAuth,
  getCoachAccessToken,
} from "@/lib/coach-auth";
import { httpLog, isDebugLogEnabled } from "@/lib/debug-log";
import { hydrateMultipartImageFields } from "@utils/reupload-images";
import { getTenantBackendUrlAndTenant } from "./utils";
import qs from "qs";

function logAxiosRequest(
  config: AxiosRequestConfig | undefined,
  status: number | string,
): void {
  if (!isDebugLogEnabled() || !config) {
    return;
  }

  const method = (config.method ?? "GET").toUpperCase();
  const path = config.url ?? "unknown";
  const query = config.params
    ? qs.stringify(config.params, { arrayFormat: "repeat" })
    : "";
  const url = query ? `${path}?${query}` : path;
  const startTime = config.metadata?.startTime;
  const durationMs =
    startTime != null ? Math.max(0, Date.now() - startTime) : undefined;

  httpLog.debug(
    durationMs != null
      ? `${method} ${url} ${status} ${durationMs}ms`
      : `${method} ${url} ${status}`,
  );
}

interface DownloadCsvOptions {
  data?: Record<string, any>;
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  isQuery?: boolean;
  isMultipart?: boolean;
  accessToken?: string;
  fileName: string;
}

// Utility to get tab-specific branch ID
const getTabSpecificBranchId = async (): Promise<string | null> => {
  if (typeof window === "undefined") return null;

  // Get unique tab ID
  let tabId = sessionStorage.getItem("tabId");
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem("tabId", tabId);
  }

  // Try tab-specific storage first
  const tabBranchId = sessionStorage.getItem(`branch_id_${tabId}`);
  if (tabBranchId) {
    return tabBranchId;
  }

  // Fallback to localStorage
  const localBranchId = localStorage.getItem("branch_id");
  if (localBranchId) {
    return localBranchId;
  }

  // Final fallback to cookie
  return await getCookie("branch_id");
};

const createAxiosInstance = (accessToken?: string): AxiosInstance => {
  const subdomain =
    typeof window !== "undefined" ? window.location.hostname.split(".")[0] : "";
  const { url, tenant } = getTenantBackendUrlAndTenant(subdomain);
  const api = axios.create({
    baseURL: `${url}/api/v2`,
  });

  // Request Interceptor
  api.interceptors.request.use(async (config) => {
    // Explicit accessToken (incl. "" for public coach login) wins over admin cookie.
    const token =
      accessToken !== undefined
        ? accessToken
        : localStorage.getItem("access_token") ||
          (await getCookie("access_token"));

    // Use tab-specific branch ID
    const branchId = await getTabSpecificBranchId();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers.branch_id = branchId || "";
    config.headers.branch = branchId || "";
    config.headers.tenant = tenant;

    // Optional: Add tab ID for backend tracking/debugging
    if (typeof window !== "undefined") {
      const tabId = sessionStorage.getItem("tabId");
      if (tabId) {
        config.headers["X-Tab-Id"] = tabId;
      }
    }

    config.metadata = { startTime: Date.now() };

    return config;
  });

  // Response Interceptor
  api.interceptors.response.use(
    (response) => {
      logAxiosRequest(response.config, response.status);
      return response;
    },
    (error) => {
      if (isDebugCaptureEnabled()) {
        recordAxiosFailure(error);
      }

      logAxiosRequest(
        error.config,
        error.response?.status ?? error.code ?? "ERR",
      );

      if (error.response?.status === 401) {
        const authHeader = String(error.config?.headers?.Authorization ?? "");
        const coachToken = getCoachAccessToken();
        const usedCoachToken =
          !!coachToken && authHeader === `Bearer ${coachToken}`;

        if (usedCoachToken) {
          clearCoachAuth();
          const parts = window.location.pathname.split("/").filter(Boolean);
          const branchKey = parts[0];
          const loginPath = branchKey
            ? `/${branchKey}/coach/login`
            : "/login";
          if (!window.location.pathname.endsWith("/coach/login")) {
            window.location.href = loginPath;
          }
        } else {
          document.cookie =
            "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          document.cookie =
            "user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
      } else {
        console.error("Error:", error);
      }
      return Promise.reject(error);
    },
  );

  return api;
};

export const fetchData = async (
  data = {},
  url: string,
  method: string,
  isQuery?: boolean,
  contentType?: boolean, // true if content type is multipart/form-data
  key?: string,
  accessToken?: string,
) => {
  const api = createAxiosInstance(accessToken);
  try {
    const body =
      !isQuery && contentType
        ? await hydrateMultipartImageFields(data as Record<string, any>)
        : data;
    const response = await api({
      method,
      url,
      headers: {
        "Content-Type": contentType
          ? "multipart/form-data"
          : "Application/json",
        "Idempotency-Key": key || undefined,
      },
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
      ...(isQuery
        ? {
            params: data,
          }
        : {
            data: body,
          }),
    });
    return response?.data || response;
  } catch (error) {
    console.error("Error fetching data2:", error);
    throw error;
  }
};

export const downloadCsv = async ({
  data = {},
  url,
  method,
  isQuery = false,
  isMultipart = false,
  accessToken,
  fileName,
}: DownloadCsvOptions): Promise<any> => {
  const api = createAxiosInstance(accessToken);

  // Use tab-specific branch ID
  const branchId = await getTabSpecificBranchId();

  try {
    const headers = {
      "Content-Type": isMultipart ? "multipart/form-data" : "application/json",
      branch: branchId as string,
    };

    const response = await api({
      method,
      url,
      headers,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
      ...(isQuery ? { params: data } : { data }),
      responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading CSV:", error);
    throw error;
  }
};

export const uploadImage = async ({
  data = {},
  url,
  method,
  isQuery,
  contentType,
  accessToken,
  setUploadProgress,
}: {
  data?: any;
  url?: string;
  method?: string;
  isQuery?: boolean;
  contentType?: boolean;
  accessToken?: string;
  setUploadProgress?: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const api = createAxiosInstance(accessToken);
  try {
    // Use tab-specific branch ID
    const branchId = await getTabSpecificBranchId();
    const body =
      !isQuery && contentType
        ? await hydrateMultipartImageFields(data as Record<string, any>)
        : data;

    const response = await api({
      method,
      url,
      headers: {
        "Content-Type": contentType
          ? "multipart/form-data"
          : "Application/json",
        branch: branchId,
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent?.total || 1),
        );
        setUploadProgress && setUploadProgress(percentCompleted);
      },
      ...(isQuery ? { params: data } : { data: body }),
    });
    return response?.data || response;
  } catch (error) {
    console.error("Error fetching data2:", error);
    throw error;
  }
};
